import * as React from 'react'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Column, ITEM_TYPE_COLUMN, SortableDragItem } from './types'
import { getColumnTypes } from './helpers'
import ColumnTypeSettings from './column_type_settings/index'
import I18n from '../../../libs/i18n'

interface ColumnRowProps {
  column: Column
  index: number
  moveColumn: (dragIndex: number, hoverIndex: number) => void
  updateColumn: (id: number | string, updates: Partial<Column>) => void
  removeColumn: (id: number | string) => void
  duplicateColumn: (id: number | string) => void
  generateOptionId: () => string
  system_column_keys: string[]
  required_eligible_system_keys: string[]
}

function generateKeyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export default function ColumnRow({
  column,
  index,
  moveColumn,
  updateColumn,
  removeColumn,
  duplicateColumn,
  generateOptionId,
  system_column_keys,
  required_eligible_system_keys,
}: ColumnRowProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [, drop] = useDrop<SortableDragItem, void, unknown>({
    accept: ITEM_TYPE_COLUMN,
    hover(item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveColumn(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag<SortableDragItem, void, { isDragging: boolean }>({
    item: { type: ITEM_TYPE_COLUMN, id: column.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const isSystemColumn = column.type === 'Projects::Columns::System'
  const isSelectColumn = column.type === 'Projects::Columns::Select'
  const requiredAllowed = !isSystemColumn || required_eligible_system_keys.includes(column.key)
  const hasSettings =
    isSystemColumn ||
    column.type === 'Projects::Columns::Text' ||
    column.type === 'Projects::Columns::Number' ||
    isSelectColumn

  const handleTypeChange = (newType: string) => {
    const updates: Partial<Column> = {
      type: newType,
      settings: {},
      column_options: newType === 'Projects::Columns::Select' ? [] : undefined,
      system_column: newType === 'Projects::Columns::System',
    }

    if (newType === 'Projects::Columns::System') {
      updates.key = ''
      updates.label = ''
      updates.required = false
    }

    updateColumn(column.id, updates)
  }

  const handleLabelChange = (label: string) => {
    const updates: Partial<Column> = { label }

    if (!isSystemColumn && (!column.key || column.key === generateKeyFromLabel(column.label))) {
      updates.key = generateKeyFromLabel(label)
    }

    updateColumn(column.id, updates)
  }

  return (
    <div
      ref={ref}
      className="column-row border rounded p-2 mb-2 bg-white"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="d-flex align-items-center">
        <i
          className="fa fa-grip-vertical text-muted mr-2"
          style={{ cursor: 'grab' }}
        />

        <select
          className="form-control form-control-sm mr-2"
          style={{ width: 120 }}
          value={column.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {getColumnTypes().map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        {!isSystemColumn && (
          <>
            <input
              type="text"
              className="form-control form-control-sm mr-2"
              style={{ flex: 1 }}
              placeholder={I18n.t('project_groups.columns_editor.label_placeholder')}
              value={column.label}
              onChange={(e) => handleLabelChange(e.target.value)}
            />
            <input
              type="text"
              className="form-control form-control-sm mr-2"
              style={{ width: 150 }}
              placeholder={I18n.t('project_groups.columns_editor.key_placeholder')}
              value={column.key}
              onChange={(e) => updateColumn(column.id, { key: e.target.value })}
            />
          </>
        )}

        {requiredAllowed && (
          <div className="form-check mr-2">
            <input
              type="checkbox"
              className="form-check-input"
              id={`required-${column.id}`}
              checked={column.required || false}
              onChange={(e) => updateColumn(column.id, { required: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor={`required-${column.id}`}>
              {I18n.t('project_groups.columns_editor.required')}
            </label>
          </div>
        )}

        <div className="ml-auto">
          {!isSystemColumn && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm mr-1"
              onClick={() => duplicateColumn(column.id)}
              title={I18n.t('project_groups.columns_editor.duplicate_column')}
            >
              <i className="fa fa-copy" />
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => removeColumn(column.id)}
            title={I18n.t('project_groups.columns_editor.remove_column')}
          >
            <i className="fa fa-trash" />
          </button>
        </div>
      </div>

      {hasSettings && (
        <div className="mt-2">
          <ColumnTypeSettings
            column={column}
            onUpdate={(updates) => updateColumn(column.id, updates)}
            generateOptionId={generateOptionId}
            system_column_keys={system_column_keys}
            required_eligible_system_keys={required_eligible_system_keys}
          />
        </div>
      )}
    </div>
  )
}
